import React, { useMemo } from 'react';
import { ISparkDeposit } from '../../../public/APIContracts/APIContracts';
import CategoryPill from 'src/components/CategoryPill/CategoryPill';
import './spark-deposits-table.scss';

interface ISparkDepositsTableProps {
  sparkDeposits: ISparkDeposit[];
  onDepositClick?: (deposit: ISparkDeposit, index: number) => void;
}

interface UpcomingDepositProps {
  upcomingDeposit: ISparkDeposit;
  formatCurrency: any;
  dueDate: string;
  statusColor: string;
}

const UpcomingDeposit = ({
  upcomingDeposit,
  formatCurrency,
  dueDate,
  statusColor,
}: UpcomingDepositProps) => {
  return (
    <div className="upcoming-deposit-container">
      <div className="upcoming-deposit-header">
        <span className="upcoming-deposit-title">Next Due</span>
      </div>
      <div className="upcoming-deposit-row">
        <div className="upcoming-deposit-name">
          <span className="upcoming-name-text">{upcomingDeposit.name}</span>
        </div>
        <div className="upcoming-deposit-amount">
          <span className="upcoming-field-label">Balance</span>
          <span className="upcoming-field-value balance-red">
            {formatCurrency}
          </span>
        </div>
        <div className="upcoming-deposit-date">
          <span className="upcoming-field-label">Due Date</span>
          <span className="upcoming-field-value">{dueDate}</span>
        </div>
        <div className="upcoming-deposit-status">
          <CategoryPill
            text={upcomingDeposit.status}
            color={statusColor}
            textColor="white"
            size="small"
            centerText={true}
          />
        </div>
      </div>
    </div>
  );
};

export const SparkDepositsTable = ({
  sparkDeposits,
  onDepositClick,
}: ISparkDepositsTableProps) => {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount / 100);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const calculateSummary = () => {
    const totalContract = sparkDeposits.reduce(
      (sum: number, deposit: ISparkDeposit) => sum + deposit.total_amount,
      0
    );
    const totalPaid = sparkDeposits.reduce(
      (sum: number, deposit: ISparkDeposit) => sum + deposit.paid_amount,
      0
    );

    const balance: number = totalContract - totalPaid;

    return { totalContract, totalPaid, balance };
  };

  const { totalContract, totalPaid, balance } = calculateSummary();

  const getStatusColor = (status: string): string => {
    switch (status.toLowerCase()) {
      case 'paid':
        return '#10b981';
      case 'payment overdue':
        return '#ef4444';
      case 'pending':
        return '#f59e0b';
      default:
        return '#6b7280';
    }
  };

  const sortedDeposits: ISparkDeposit[] = useMemo(() => {
    return [...sparkDeposits].sort((a: ISparkDeposit, b: ISparkDeposit) => {
      if (a.status === 'Payment Overdue' && b.status !== 'Payment Overdue') {
        return -1;
      }

      if (b.status === 'Payment Overdue' && a.status !== 'Payment Overdue') {
        return 1;
      }

      if (a.due_date && b.due_date) {
        return new Date(a.due_date).getTime() - new Date(b.due_date).getTime();
      }

      return 0;
    });
  }, [sparkDeposits]);

  const upcomingDeposit: ISparkDeposit | undefined = useMemo(() => {
    return sparkDeposits
      .filter(
        (deposit: ISparkDeposit) =>
          deposit.status !== 'Paid' && deposit.due_date
      )
      .sort((a: ISparkDeposit, b: ISparkDeposit) => {
        if (a.due_date && b.due_date) {
          return (
            new Date(a.due_date).getTime() - new Date(b.due_date).getTime()
          );
        }
        return 0;
      })[0];
  }, [sparkDeposits]);

  return (
    <div className="spark-deposits-table">
      {/* Summary Section. */}
      <div className="deposits-summary">
        <div className="summary-metrics-container">
          <div className="summary-metric">
            <span className="summary-metric-label">Deposit Total</span>
            <span className="summary-metric-value">
              {formatCurrency(totalContract)}
            </span>
          </div>
          <div className="summary-metric">
            <span className="summary-metric-label">Total Paid</span>
            <span className="summary-metric-value">
              {formatCurrency(totalPaid)}
            </span>
          </div>
          <div className="summary-metric">
            <span className="summary-metric-label">Balance</span>
            <span className="summary-metric-value balance-red">
              {formatCurrency(balance)}
            </span>
          </div>
        </div>
        {/* Upcoming deposit section. */}
        {upcomingDeposit && (
          <UpcomingDeposit
            upcomingDeposit={upcomingDeposit}
            formatCurrency={formatCurrency(upcomingDeposit.owed_amount)}
            dueDate={formatDate(upcomingDeposit.due_date)}
            statusColor={getStatusColor(upcomingDeposit.status)}
          />
        )}
      </div>
      {/* Deposits Table. */}
      <div className="deposits-table-container">
        <table className="deposits-table">
          <thead className="table-header">
            <tr>
              <th>Deposit</th>
              <th>Balance</th>
              <th>Due Date</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {sortedDeposits.map((deposit: ISparkDeposit, index: number) => (
              <tr
                key={`deposit-${deposit.id}-${index}`}
                className={`table-row ${onDepositClick ? 'clickable' : ''} ${
                  deposit.status === 'Payment Overdue' ? 'overdue' : ''
                }`}
              >
                <td className="deposit-name-cell">
                  <span className="deposit-name-text" title={deposit.name}>
                    {deposit.name}
                  </span>
                </td>
                <td className="amount-cell">
                  {formatCurrency(deposit.owed_amount)}
                </td>
                <td className="date-cell">{formatDate(deposit.due_date)}</td>
                <td className="status-cell">
                  <CategoryPill
                    text={deposit.status}
                    color={getStatusColor(deposit.status)}
                    textColor="white"
                    size="small"
                    centerText={true}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {sparkDeposits.length === 0 && (
        <div className="no-deposits-message">
          <p>No deposits found for this unit.</p>
        </div>
      )}
    </div>
  );
};
